// Global Instructions Rule Applied!
//
// Fund full-form Ledger tab (A3 item 2, verification form): the trial
// balance of the fund's chart of accounts - opening / debits / credits /
// closing per account for a selected financial year (optionally capped at an
// as-of date) - with per-account drill-down into the journal lines behind
// the balance, each line linking back to its source record. Covers BOTH
// journal engines (asset-side at__ and fund-side ft__) since they post
// through the same chart of accounts.
//
// The headline verification signal is the Balanced tag: the sum of all
// closing balances must be zero (double entry). Balances are debit-positive
// signed sums (Assets/Expenses positive, Liabilities/Equity/Income
// negative) - the raw ledger view, deliberately unpolished.
//
// SHARED COMPONENT (EqualizationSection precedent): the optional `group`
// prop switches the tab into CONSOLIDATED mode for the fund-group form -
// a WORKPAPER trial balance (one closing column per member fund, primary
// first, then Eliminations, then the consolidated set), rows keyed on the
// GL catalogue account, drill-down fanning out across member funds with
// intercompany rows flagged (they contribute nothing to the running
// balance). The FY selector lists the PRIMARY fund's years; FY close /
// reopen stays per-fund and is hidden here. Without `group` the fund path
// is unchanged.
//
// The partner allocation lines a year close books on a fund on the
// Financial year basis (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md 4.2) net
// to nil on every account; the "Show partner allocation" switch shows them
// in the movement and the drill-down (hidden by default, both modes). The
// Close year confirm is CloseFinYearModal: it shows the allocation the close
// will book, per partner and tax category, with the GP's overrides.
//
// Lightweight fetch-and-render recipe (CapitalCallsTab precedent):
// tab-local state, no redux slice.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Select, Switch, Table, Tag, Typography } from 'antd'
import DatePicker from '../../../../../core/components/DatePicker.tsx'
import { DownloadOutlined, LockOutlined, ReloadOutlined, UnlockOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { useNumberDisplay } from '../../../../../core/utils/inputNumberFormat.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_FUND_TRANSACTIONS } from '../../../../../state_manager/globals.ts'
import { exportJsonToExcel } from '../../../../../core/services/utilitiesService.ts'
import { getFundNameById } from '../../../../../core/utils/lookupLabelFromStore.ts'
import ExportRowsButton from '../../../../shared/table_grid_export/ExportRowsButton.tsx'
import CloseFinYearModal from './CloseFinYearModal.tsx'
import type { FinYearAllocationOverride } from '../../../../../state_manager/data_models/fund_fin_year_allocation.ts'
import {
  fetchFundTrialBalance,
  fetchFundGlDetail,
  fetchFundFinYears,
  closeFundFinYear,
  reopenFundFinYear
} from '../../../services/fundsService.ts'
import {
  fetchFundGroupTrialBalance,
  fetchFundGroupGlDetail
} from '../../../../fund_groups/services/fundGroupsService.ts'
import type {
  FundTrialBalanceRow,
  FundLedgerFinYear,
  FundLedgerTotals,
  FundGlDetailRow
} from '../../../../../state_manager/data_models/fund_ledger.ts'
import type {
  FundGroupTrialBalanceRow,
  FundGroupLedgerMember,
  FundGroupMemberFinYear,
  FundGroupGlDetailRow
} from '../../../../../state_manager/data_models/fund_group_ledger.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const disp = (v: unknown): string => (v != null && v !== '' ? String(v) : '-')

interface LedgerTabProps {
  fundId: string | null
  /** Consolidated mode (fund-group form): render the workpaper trial
   *  balance across member funds. FY options come from the primary fund. */
  group?: { fundGroupId: string; primaryFundId: string | null }
}

type LedgerRow = FundTrialBalanceRow | FundGroupTrialBalanceRow
type LedgerDetailRow = FundGlDetailRow | FundGroupGlDetailRow

export default function LedgerTab({ fundId, group }: LedgerTabProps) {
  const isGroup = Boolean(group)
  const currencyText = useNumberDisplay('currency')
  const { openFormInTab } = useTabManagerContext()

  const [rows, setRows] = useState<LedgerRow[]>([])
  const [members, setMembers] = useState<FundGroupLedgerMember[]>([])
  const [memberFinYears, setMemberFinYears] = useState<FundGroupMemberFinYear[]>([])
  const [finYear, setFinYear] = useState<FundLedgerFinYear | null>(null)
  const [totals, setTotals] = useState<(FundLedgerTotals & { eliminations?: number }) | null>(null)
  const [fyOptions, setFyOptions] = useState<Array<{ value: string; label: string }>>([])
  const [selectedFy, setSelectedFy] = useState<string | null>(null)
  const [asOf, setAsOf] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // the partner allocation lines of a year close: hidden by default. The
  // ref is what `load` reads, so flipping the switch does not re-create
  // `load` and re-fire the mount effect (which would reset the year)
  const [showAllocation, setShowAllocation] = useState(false)
  const showAllocationRef = useRef(false)
  const [closeOpen, setCloseOpen] = useState(false)

  // lazily-fetched drill-down per account (chart_of_accounts_id, or
  // gl_account_id in group mode -> rows)
  const [detail, setDetail] = useState<Record<string, { rows: LedgerDetailRow[]; openingFmt: string | null }>>({})

  // the row key: per-fund COA row vs GL catalogue account (group mode)
  const rowId = useCallback(
    (r: LedgerRow): string =>
      'acc_gl_accounts__id' in r ? r.acc_gl_accounts__id : r.acc_chart_of_accounts__id,
    []
  )

  const load = useCallback(
    async (fyId: string | null, asOfDate: string | null, showAlloc: boolean = showAllocationRef.current) => {
      if (!fundId && !group) return
      setLoading(true)
      try {
        const token = getAccessToken(store.getState().auth)
        if (group) {
          const [tb, fy] = await Promise.all([
            fetchFundGroupTrialBalance(group.fundGroupId, { fyId, asOf: asOfDate, showAllocation: showAlloc }, token),
            group.primaryFundId
              ? fetchFundFinYears(group.primaryFundId, token)
              : Promise.resolve({ data: [] as Array<{ value: string; label: string }> })
          ])
          if (tb.error) showErrorToast(tb.error)
          setRows(tb.data)
          setMembers(tb.members)
          setMemberFinYears(tb.memberFinYears)
          setFinYear(tb.finYear)
          setTotals(tb.totals)
          setFyOptions(fy.data)
          if (!fyId && tb.finYear?.id) setSelectedFy(tb.finYear.id)
        } else if (fundId) {
          const [tb, fy] = await Promise.all([
            fetchFundTrialBalance(fundId, { fyId, asOf: asOfDate, showAllocation: showAlloc }, token),
            fetchFundFinYears(fundId, token)
          ])
          if (tb.error) showErrorToast(tb.error)
          setRows(tb.data)
          setFinYear(tb.finYear)
          setTotals(tb.totals)
          setFyOptions(fy.data)
          if (!fyId && tb.finYear?.id) setSelectedFy(tb.finYear.id)
        }
        setDetail({})
      } finally {
        setLoading(false)
      }
    },
    [fundId, group]
  )

  useEffect(() => {
    void load(null, null)
  }, [load])

  const refresh = useCallback(() => {
    void load(selectedFy, asOf)
  }, [load, selectedFy, asOf])

  // year-end close / reopen (fund__close_fin_year, spec A3.4): on the
  // Financial year basis the close books the partner allocation first
  // (CloseFinYearModal shows it, with the GP's overrides), then the closing
  // journals move the year's P&L into Accumulated net income and lock the
  // year for booking; reopen reverses both. Server enforces oldest-first
  // close / newest-first reopen. Per-fund only - hidden in consolidated mode.
  const handleCloseReopenConfirm = useCallback(
    async (overrides: FinYearAllocationOverride[] | undefined) => {
      if (!fundId || !finYear?.id) return
      const closing = !finYear.closed
      const token = getAccessToken(store.getState().auth)
      const res = closing
        ? await closeFundFinYear(fundId, finYear.id, token, overrides)
        : await reopenFundFinYear(fundId, finYear.id, token)
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      setCloseOpen(false)
      const msg = (res.data as { status_message?: string } | null)?.status_message
      if (msg) toast.success(msg)
      void load(selectedFy, asOf)
    },
    [fundId, finYear, selectedFy, asOf, load]
  )

  const loadDetail = useCallback(
    async (accountId: string) => {
      if (detail[accountId]) return
      const token = getAccessToken(store.getState().auth)
      if (group) {
        const res = await fetchFundGroupGlDetail(
          group.fundGroupId,
          accountId,
          { fyId: selectedFy, asOf, showAllocation },
          token
        )
        if (res.error) {
          showErrorToast(res.error)
          return
        }
        setDetail((prev) => ({
          ...prev,
          [accountId]: { rows: res.data, openingFmt: res.openingFmt ?? null }
        }))
      } else if (fundId) {
        const res = await fetchFundGlDetail(fundId, accountId, { fyId: selectedFy, asOf, showAllocation }, token)
        if (res.error) {
          showErrorToast(res.error)
          return
        }
        setDetail((prev) => ({
          ...prev,
          [accountId]: { rows: res.data, openingFmt: res.openingFmt ?? null }
        }))
      }
    },
    [fundId, group, selectedFy, asOf, detail, showAllocation]
  )

  const handleExport = useCallback(async () => {
    const token = getAccessToken(store.getState().auth)
    if (isGroup) {
      const memberCols = members.map((m) => m.funds__name)
      const data = (rows as FundGroupTrialBalanceRow[]).map((r) => {
        const base: Record<string, unknown> = {
          Type: r.cfg_acc_account_types__label ?? '',
          Code: r.acc_gl_accounts__account_code ?? '',
          Account: r.acc_gl_accounts__account_name
        }
        members.forEach((m) => {
          const cell = r.prog__members.find((c) => c.funds__id === m.funds__id)
          base[m.funds__name] = cell?.prog__closing ?? ''
        })
        base['Eliminations'] = r.prog__eliminations
        base['Opening'] = r.prog__opening
        base['Debits'] = r.prog__debits
        base['Credits'] = r.prog__credits
        base['Closing'] = r.prog__closing
        return base
      })
      const res = await exportJsonToExcel(
        {
          data,
          columns: ['Type', 'Code', 'Account', ...memberCols, 'Eliminations', 'Opening', 'Debits', 'Credits', 'Closing'],
          filename: `consolidated_trial_balance_${finYear?.fin_year ?? ''}`
        },
        token
      )
      if (!res.success) showErrorToast(res.error || 'Export failed')
      return
    }
    const data = (rows as FundTrialBalanceRow[]).map((r) => ({
      Type: r.cfg_acc_account_types__label ?? '',
      Code: r.acc_gl_accounts__account_code ?? '',
      Account: r.acc_gl_accounts__account_name,
      Opening: r.prog__opening,
      Debits: r.prog__debits,
      Credits: r.prog__credits,
      Closing: r.prog__closing
    }))
    const res = await exportJsonToExcel(
      {
        data,
        columns: ['Type', 'Code', 'Account', 'Opening', 'Debits', 'Credits', 'Closing'],
        filename: `trial_balance_${finYear?.fin_year ?? ''}`
      },
      token
    )
    if (!res.success) showErrorToast(res.error || 'Export failed')
  }, [rows, finYear, isGroup, members])

  // group rows by account type with section header rows
  type DisplayRow = ({ kind: 'account' } & LedgerRow) | { kind: 'section'; rowId: string; label: string }

  const displayRows: DisplayRow[] = useMemo(() => {
    const out: DisplayRow[] = []
    let lastType: string | null = null
    for (const r of rows) {
      const type = r.cfg_acc_account_types__label ?? 'Other'
      if (type !== lastType) {
        out.push({ kind: 'section', rowId: `section-${type}`, label: type })
        lastType = type
      }
      out.push({ kind: 'account', ...r })
    }
    return out
  }, [rows])

  const isCash = (row: LedgerRow) => row.acc_gl_accounts__account_name === 'Cash'

  const columns = useMemo(() => {
    const accountCol = {
      title: 'Account',
      dataIndex: 'acc_gl_accounts__account_name',
      render: (_: unknown, row: DisplayRow) =>
        row.kind === 'section' ? (
          <Text strong className='text-gray-600'>
            {row.label}
          </Text>
        ) : (
          <span className={isCash(row) ? 'font-semibold' : ''} style={{ paddingLeft: 12 }}>
            {row.acc_gl_accounts__account_code ? `${row.acc_gl_accounts__account_code} · ` : ''}
            {row.acc_gl_accounts__account_name}
          </span>
        )
    }
    const numCol = (title: string, fmtKey: string) => ({
      title,
      dataIndex: fmtKey,
      width: 140,
      align: 'right' as const,
      render: (_: unknown, row: DisplayRow) =>
        row.kind === 'section' ? '' : disp((row as unknown as Record<string, unknown>)[fmtKey])
    })
    if (!isGroup) {
      return [
        accountCol,
        numCol('Opening', 'prog__opening_fmt'),
        numCol('Debits', 'prog__debits_fmt'),
        numCol('Credits', 'prog__credits_fmt'),
        {
          ...numCol('Closing', 'prog__closing_fmt'),
          width: 150,
          render: (_: unknown, row: DisplayRow) =>
            row.kind === 'section' ? (
              ''
            ) : (
              <span className={isCash(row) ? 'font-semibold' : ''}>
                {disp((row as FundTrialBalanceRow).prog__closing_fmt)}
              </span>
            )
        }
      ]
    }
    // workpaper: one closing column per member (primary first), then
    // Eliminations, then the consolidated set
    const memberCols = members.map((m) => ({
      title: m.funds__name,
      key: `member-${m.funds__id}`,
      width: 130,
      align: 'right' as const,
      render: (_: unknown, row: DisplayRow) => {
        if (row.kind === 'section') return ''
        const cell = (row as FundGroupTrialBalanceRow).prog__members.find((c) => c.funds__id === m.funds__id)
        return disp(cell?.prog__closing_fmt)
      }
    }))
    return [
      accountCol,
      ...memberCols,
      numCol('Eliminations', 'prog__eliminations_fmt'),
      numCol('Opening', 'prog__opening_fmt'),
      numCol('Debits', 'prog__debits_fmt'),
      numCol('Credits', 'prog__credits_fmt'),
      {
        ...numCol('Consolidated', 'prog__closing_fmt'),
        width: 150,
        render: (_: unknown, row: DisplayRow) =>
          row.kind === 'section' ? (
            ''
          ) : (
            <span className={isCash(row) ? 'font-semibold' : ''}>
              {disp((row as FundGroupTrialBalanceRow).prog__closing_fmt)}
            </span>
          )
      }
    ]
  }, [isGroup, members])

  const detailColumns = useMemo(() => {
    const cols: Array<Record<string, unknown>> = []
    if (isGroup) {
      cols.push({
        title: 'Fund',
        dataIndex: 'funds__name',
        width: 110,
        render: (v: unknown, row: LedgerDetailRow) => (
          <span>
            {disp(v)}{' '}
            {(row as FundGroupGlDetailRow).prog__intercompany ? (
              <Tag color='orange' style={{ marginLeft: 4 }}>
                IC
              </Tag>
            ) : null}
          </span>
        )
      })
    }
    cols.push(
      { title: 'Date', dataIndex: 'acc_journals__journal_date_fmt', width: 100, render: disp },
      { title: 'Description', dataIndex: 'acc_journals__description', render: disp },
      {
        title: 'Source',
        dataIndex: 'prog__source_type',
        width: 240,
        render: (_: unknown, row: LedgerDetailRow) => (
          <span>
            {row.fund_transactions__id ? (
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() =>
                  openFormInTab(TABLE_FUND_TRANSACTIONS, row.fund_transactions__id as string, row.prog__source_type ?? 'Transaction')
                }
              >
                {disp(row.prog__source_type)}
              </button>
            ) : (
              disp(row.prog__source_type)
            )}
            {row.investors__name ? <Text type='secondary'> — {row.investors__name}</Text> : null}
          </span>
        )
      },
      { title: 'Debit', dataIndex: 'prog__debit_fmt', width: 120, align: 'right' as const, render: disp },
      { title: 'Credit', dataIndex: 'prog__credit_fmt', width: 120, align: 'right' as const, render: disp },
      {
        title: 'Balance',
        dataIndex: 'prog__running_balance_fmt',
        width: 130,
        align: 'right' as const,
        render: disp
      }
    )
    return cols
  }, [isGroup, openFormInTab])

  if (!fundId && !group) {
    return <div className='p-6 text-gray-500'>Save the fund first to see its ledger.</div>
  }

  // a drill-down export's file name: the fund (or consolidated fund), the account, 'journal lines'
  const ledgerName = group
    ? store.getState().globalConfigs?.fund_groups?.find((g) => g.id === group.fundGroupId)?.name || 'Consolidated fund'
    : getFundNameById(fundId ?? '') || 'Fund'
  const journalLinesExportName = (r: LedgerRow): string =>
    [ledgerName, r.acc_gl_accounts__account_code, r.acc_gl_accounts__account_name, 'journal lines']
      .filter(Boolean)
      .join(' ')

  // summary cell layout: account col, [member cols + eliminations] (group),
  // opening, debits, credits, closing
  const summaryCells: React.ReactNode[] = []
  if (totals) {
    summaryCells.push(<span key='label'>Total (Σ = 0 when balanced)</span>)
    if (isGroup) {
      members.forEach((m) => summaryCells.push(<span key={`m-${m.funds__id}`} />))
      summaryCells.push(<span key='elim'>{currencyText(totals.eliminations ?? 0)}</span>)
    }
    summaryCells.push(
      <span key='opening'>{currencyText(totals.opening)}</span>,
      <span key='debits'>{currencyText(totals.debits)}</span>,
      <span key='credits'>{currencyText(totals.credits)}</span>,
      <span key='closing'>{currencyText(totals.closing)}</span>
    )
  }

  return (
    <div className='h-full overflow-y-auto p-3'>
      {fundId && finYear ? (
        <CloseFinYearModal
          open={closeOpen}
          fundId={fundId}
          finYear={finYear}
          onConfirm={handleCloseReopenConfirm}
          onCancel={() => setCloseOpen(false)}
        />
      ) : null}
      <FormSection
        title={isGroup ? 'Consolidated Trial Balance' : 'Trial Balance'}
        titleColor='blue'
        loading={loading}
        headerActions={
          <div className='flex items-center gap-2'>
            <Select
              size='small'
              style={{ minWidth: 130 }}
              value={selectedFy}
              options={fyOptions}
              placeholder='Financial year'
              onChange={(v) => {
                setSelectedFy(v)
                void load(v, asOf)
              }}
            />
            <DatePicker
              size='small'
              value={asOf ? dayjs(asOf) : null}
              format={DATE_FORMAT}
              placeholder='As of (FY end)'
              onChange={(d) => {
                const v = d ? d.format('YYYY-MM-DD') : null
                setAsOf(v)
                void load(selectedFy, v)
              }}
            />
            <span className='flex items-center gap-1'>
              <Switch
                size='small'
                checked={showAllocation}
                aria-label='Show partner allocation lines'
                onChange={(v) => {
                  setShowAllocation(v)
                  showAllocationRef.current = v
                  void load(selectedFy, asOf, v)
                }}
              />
              <Text type='secondary' className='text-xs'>
                Show partner allocation
              </Text>
            </span>
            {!isGroup && finYear ? (
              <Button
                size='small'
                icon={finYear.closed ? <UnlockOutlined /> : <LockOutlined />}
                onClick={() => setCloseOpen(true)}
              >
                {finYear.closed ? 'Reopen year' : 'Close year'}
              </Button>
            ) : null}
            <Button size='small' icon={<ReloadOutlined />} onClick={refresh}>
              Refresh
            </Button>
            <Button size='small' icon={<DownloadOutlined />} onClick={() => void handleExport()} disabled={rows.length === 0}>
              Export
            </Button>
          </div>
        }
      >
        {finYear ? (
          <div className='mb-2 flex items-center gap-3'>
            <Text type='secondary'>
              FY {finYear.fin_year}
              {finYear.year_start_date
                ? ` (${dayjs(finYear.year_start_date).format(DATE_FORMAT)} – ${dayjs(
                    finYear.window_end ?? finYear.year_end_date
                  ).format(DATE_FORMAT)})`
                : ''}
              {finYear.closed ? ' — closed' : ''}
            </Text>
            {totals ? (
              totals.balanced ? (
                <Tag color='green'>Balanced</Tag>
              ) : (
                <Tag color='red'>Off by {currencyText(totals.closing)}</Tag>
              )
            ) : null}
            {isGroup && memberFinYears.length > 0 ? (
              <Text type='secondary'>
                {memberFinYears
                  .map((m) =>
                    m.acc_fin_years__fin_year == null
                      ? `${m.funds__name}: no FY ${finYear.fin_year}`
                      : `${m.funds__name}: FY ${m.acc_fin_years__fin_year}${m.acc_fin_years__closed ? ' (closed)' : ''}`
                  )
                  .join(' · ')}
              </Text>
            ) : null}
          </div>
        ) : null}

        {rows.length === 0 && !loading ? (
          <Text type='secondary'>
            No journals yet — book a fund transaction, issue a capital call or record a payment and
            the balances appear here.
          </Text>
        ) : (
          <Table
            size='small'
            columns={columns as never}
            dataSource={displayRows}
            pagination={false}
            rowKey={(row: DisplayRow) => (row.kind === 'section' ? row.rowId : rowId(row))}
            expandable={{
              rowExpandable: (row: DisplayRow) => row.kind === 'account',
              onExpand: (expanded, row: DisplayRow) => {
                if (expanded && row.kind === 'account') void loadDetail(rowId(row))
              },
              expandedRowRender: (row: DisplayRow) => {
                if (row.kind !== 'account') return null
                const d = detail[rowId(row)]
                if (!d) return <Text type='secondary'>Loading journal lines…</Text>
                return (
                  <div className='py-1'>
                    <div className='mb-1 flex items-center justify-between gap-2'>
                      <Text type='secondary'>
                        Opening balance: {disp(d.openingFmt)}
                        {isGroup ? ' (consolidated, intercompany eliminated)' : ''}
                      </Text>
                      <ExportRowsButton
                        name={journalLinesExportName(row)}
                        rows={d.rows}
                        columns={[
                          { title: 'Fund', value: (r) => ('funds__name' in r ? r.funds__name : null), when: isGroup },
                          {
                            title: 'Intercompany',
                            value: (r) => ('prog__intercompany' in r && r.prog__intercompany ? 'Yes' : null),
                            when: isGroup
                          },
                          { title: 'Date', value: (r) => r.acc_journals__journal_date, kind: 'date' },
                          { title: 'Description', value: (r) => r.acc_journals__description },
                          { title: 'Source', value: (r) => r.prog__source_type },
                          // the Source cell's secondary text: the line's LP
                          { title: 'Investor', value: (r) => r.investors__name },
                          { title: 'Debit', value: (r) => r.prog__debit },
                          { title: 'Credit', value: (r) => r.prog__credit },
                          { title: 'Balance', value: (r) => r.prog__running_balance }
                        ]}
                      />
                    </div>
                    {d.rows.length === 0 ? (
                      <Text type='secondary'>No journal lines in this period.</Text>
                    ) : (
                      <Table
                        size='small'
                        columns={detailColumns as never}
                        dataSource={d.rows}
                        pagination={false}
                        rowKey='acc_journals__id'
                      />
                    )}
                    {isGroup ? (
                      <Text type='secondary' className='block mt-1'>
                        IC-tagged rows are the intercompany pass-through — shown for the audit trail
                        but excluded from the running balance.
                      </Text>
                    ) : null}
                  </div>
                )
              }
            }}
            summary={() =>
              totals ? (
                <Table.Summary fixed>
                  <Table.Summary.Row className='font-medium'>
                    {summaryCells.map((cell, i) => (
                      <Table.Summary.Cell key={i} index={i} align={i === 0 ? 'left' : 'right'}>
                        {cell}
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        )}
        <Text type='secondary' className='block mt-2'>
          {isGroup
            ? 'Workpaper view: one closing column per member fund (raw per-fund books), the intercompany eliminations, and the consolidated balances. Balances are debit-positive. Expand an account for the journal lines behind its balance.'
            : 'Balances are debit-positive: assets and expenses show positive, liabilities, equity and income negative. Expand an account for the journal lines behind its balance.'}
        </Text>
      </FormSection>
    </div>
  )
}
